import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import NavigationSidebar from '../../components/ui/NavigationSidebar';
import BreadcrumbTrail from '../../components/ui/BreadcrumbTrail';
import DataStreamPanel from './components/DataStreamPanel';
import DataQualityPanel from './components/DataQualityPanel';
import PlatformHealthMatrix from './components/PlatformHealthMatrix';
import HistoricalPerformance from './components/HistoricalPerformance';
import AlertConfiguration from './components/AlertConfiguration';
import { RefreshCw, Download, Bell } from 'lucide-react';

const DataCollectionMonitor = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [refreshInterval, setRefreshInterval] = useState(5000);
  const [showAlertConfig, setShowAlertConfig] = useState(false);

  const [monitoringData, setMonitoringData] = useState({
    dataStreams: [],
    qualityMetrics: {},
    platformHealth: [],
    historicalData: []
  });

  const loadMonitoringData = async () => {
    try {
      setLoading(true);
      
      // Simulate real-time monitoring data
      const mockData = {
        dataStreams: [
          {
            platform: 'Facebook',
            throughput: 125,
            errorRate: 0.8,
            latency: 245,
            status: 'healthy',
            lastUpdate: new Date()?.toISOString()
          },
          {
            platform: 'LinkedIn',
            throughput: 78,
            errorRate: 1.2,
            latency: 189,
            status: 'healthy',
            lastUpdate: new Date()?.toISOString()
          },
          {
            platform: 'Twitter/X',
            throughput: 0,
            errorRate: 100,
            latency: 0,
            status: 'error',
            lastUpdate: new Date(Date.now() - 7200000)?.toISOString()
          },
          {
            platform: 'Reddit',
            throughput: 203,
            errorRate: 2.1,
            latency: 312,
            status: 'healthy',
            lastUpdate: new Date()?.toISOString()
          },
          {
            platform: 'Instagram',
            throughput: 45,
            errorRate: 4.5,
            latency: 567,
            status: 'warning',
            lastUpdate: new Date()?.toISOString()
          }
        ],
        qualityMetrics: {
          sentimentAccuracy: 92.4,
          duplicateRate: 3.2,
          classificationSuccess: 96.8,
          dataCompleteness: 94.5,
          averageConfidence: 89.7
        },
        platformHealth: [
          { platform: 'Facebook', connectivity: 100, rateLimit: 45, freshness: 98 },
          { platform: 'LinkedIn', connectivity: 100, rateLimit: 32, freshness: 95 },
          { platform: 'Twitter/X', connectivity: 0, rateLimit: 0, freshness: 0 },
          { platform: 'Reddit', connectivity: 100, rateLimit: 67, freshness: 99 },
          { platform: 'Instagram', connectivity: 100, rateLimit: 89, freshness: 82 }
        ]
      };

      setMonitoringData(mockData);
    } catch (error) {
      console.error('Error loading monitoring data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user?.id) {
      loadMonitoringData();
    }
  }, [user]);

  useEffect(() => {
    if (autoRefresh) {
      const interval = setInterval(loadMonitoringData, refreshInterval);
      return () => clearInterval(interval);
    }
  }, [autoRefresh, refreshInterval]);

  const exportReport = () => {
    const report = {
      timestamp: new Date()?.toISOString(),
      ...monitoringData
    };
    const blob = new Blob([JSON.stringify(report, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `monitoring-report-${Date.now()}.json`;
    a?.click();
  };

  const breadcrumbs = [
    { label: 'Dashboard', path: '/live-dashboard' },
    { label: 'Data Collection Monitor', path: '/data-collection-monitor' }
  ];

  return (
    <div className="flex h-screen bg-gray-50">
      <NavigationSidebar isCollapsed={false} onToggle={() => {}} />
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Header */}
        <header className="bg-white border-b border-gray-200 px-6 py-4">
          <BreadcrumbTrail items={breadcrumbs} />
          <div className="flex items-center justify-between mt-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Data Collection Monitor</h1>
              <p className="text-sm text-gray-600 mt-1">
                Real-time oversight of social intelligence gathering and data quality
              </p>
            </div>
            <div className="flex gap-3">
              <label className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50">
                <input
                  type="checkbox"
                  checked={autoRefresh}
                  onChange={(e) => setAutoRefresh(e?.target?.checked)}
                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <span className="text-sm text-gray-700">Auto-refresh</span>
              </label>
              
              <button
                onClick={() => setShowAlertConfig(true)}
                className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <Bell className="w-4 h-4" />
                Alerts
              </button>

              <button
                onClick={exportReport}
                className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <Download className="w-4 h-4" />
                Export
              </button>

              <button
                onClick={loadMonitoringData}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                <RefreshCw className="w-4 h-4" />
                Refresh
              </button>
            </div>
          </div>
        </header>

        {/* Main Content */}
        <main className="flex-1 overflow-auto p-6 space-y-6">
          {/* Live Status Indicator */}
          <div className="flex items-center gap-2 text-sm">
            <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
            <span className="text-gray-600">
              Live monitoring active • Updates every {refreshInterval / 1000}s
            </span>
          </div>

          {/* Data Streams */}
          <DataStreamPanel streams={monitoringData?.dataStreams} />

          {/* Data Quality */}
          <DataQualityPanel metrics={monitoringData?.qualityMetrics} />

          {/* Platform Health Matrix */}
          <PlatformHealthMatrix platforms={monitoringData?.platformHealth} />

          {/* Historical Performance */}
          <HistoricalPerformance />
        </main>
      </div>
      {/* Alert Configuration Modal */}
      {showAlertConfig && (
        <AlertConfiguration
          onClose={() => setShowAlertConfig(false)}
          onSave={(config) => {
            console.log('Alert config saved:', config);
            setShowAlertConfig(false);
          }}
        />
      )}
    </div>
  );
};

export default DataCollectionMonitor;